import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { AnchoredMarkPopover } from '../../climbs/climb-editor/AnchoredMarkPopover';
import { RouteHoldsCanvas } from './RouteHoldsCanvas';
import { useRouteHolds } from './useRouteHolds';
import type { RouteHold } from '../../../types/competition-round';

interface RouteHoldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The route's name, for the heading. */
  name: string;
  imageUrl: string;
  /** The drawing as it is saved. */
  holds: RouteHold[];
  saving?: boolean;
  /** Saves the drawing whole; the server counts the circles into `holds_count`. */
  onSave: (holds: RouteHold[]) => void | Promise<void>;
}

/**
 * Where an admin circles a lead route's holds.
 *
 * **The circles are the route's holds count.** There is no number to type here:
 * placing the twenty-eighth circle is what makes the route twenty-eight holds
 * long, and that is also what lets the round be started (see
 * `Lifecycle::Round::RoutesNotReady`). So the count is shown, not asked.
 *
 * A draft until saved, like every other dialog here: drawing and then walking
 * away decides nothing, and the circles are reset from the route each time it
 * opens.
 */
export function RouteHoldsDialog({
  open,
  onOpenChange,
  name,
  imageUrl,
  holds,
  saving = false,
  onSave,
}: RouteHoldsDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RouteHold[]>(holds);

  // Every opening starts from what is actually saved, done while rendering so the
  // picture never shows a stale drawing for a frame.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(holds);
  }

  const editor = useRouteHolds({ holds: draft, onChange: setDraft });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {name}
            <span
              className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground"
              data-testid="route-holds-total"
            >
              {t('leadRounds.holdsOnRoute', { count: draft.length })}
            </span>
          </DialogTitle>
          <DialogDescription>{t('leadRounds.draw.description')}</DialogDescription>
        </DialogHeader>

        {/* The picture, and the menu that hangs off whichever circle is being
            held down. The popover sits beside the transformed layer rather than
            in it, so it does not swell as the photo is zoomed. */}
        <div className="relative">
          <RouteHoldsCanvas
            editor={editor}
            imageUrl={imageUrl}
            holdLabel={(number) => t('leadRounds.draw.holdLabel', { number })}
            resetZoomLabel={t('leadRounds.draw.resetZoom')}
            testId="route-holds-canvas"
            className="max-h-[60svh]"
          />

          {editor.menu && (
            <AnchoredMarkPopover
              at={editor.menu.at}
              radius={editor.menu.radius}
              view={editor.zoom.view}
              frame={editor.zoom.frame}
              testId="route-hold-menu"
            >
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => editor.deleteAt(editor.menu!.index)}
                data-testid="delete-route-hold"
              >
                <Trash2 className="size-3.5" aria-hidden />
                {t('leadRounds.draw.deleteHold')}
              </Button>
            </AnchoredMarkPopover>
          )}
        </div>

        <p className="text-xs text-muted-foreground">{t('leadRounds.draw.help')}</p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSave(draft)} disabled={saving} data-testid="save-route-holds">
            {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
